import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage, LANGUAGES } from '../../contexts/LanguageContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { Menu, X, Ticket, Globe, ChevronDown, User, LayoutDashboard, Shield, LogOut, Check, Settings } from 'lucide-react';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { t, lang, setLanguage } = useLanguage();
  const { currency, setCurrency } = useCurrency();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const navigate = useNavigate();
  const langRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  const currentLang = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0];

  return (
    <nav className="glass-nav fixed top-0 left-0 right-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <span className="font-black text-xl md:text-2xl tracking-tighter uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              <span style={{ color: '#E63946' }}>SAC</span>
              <span className="text-white"> EVENTS</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/explore" className="text-sm font-semibold uppercase tracking-wide text-gray-300 hover:text-white transition-colors" data-testid="nav-explore">
              {t('explore')}
            </Link>
            <Link to="/feed" className="text-sm font-semibold uppercase tracking-wide text-gray-300 hover:text-white transition-colors" data-testid="nav-feed">
              Feed
            </Link>
            {user && (
              <Link to="/my-tickets" className="text-sm font-semibold uppercase tracking-wide text-gray-300 hover:text-white transition-colors" data-testid="nav-my-tickets">
                {t('myTickets')}
              </Link>
            )}
            {(user?.role === 'organiser' || user?.role === 'super_admin') && (
              <Link to="/organiser" className="text-sm font-semibold uppercase tracking-wide text-gray-300 hover:text-white transition-colors" data-testid="nav-organiser">
                {t('organiserDashboard')}
              </Link>
            )}
            {(['organiser', 'super_admin'].includes(user?.role) || user?.is_dj) && (
              <Link to="/recurring-series" className="text-sm font-semibold uppercase tracking-wide text-gray-300 hover:text-white transition-colors" data-testid="nav-recurring-series">
                {user?.is_dj && user?.role !== 'organiser' && user?.role !== 'super_admin' ? 'DJ Dashboard' : 'Series'}
              </Link>
            )}
            {user && (
              <Link to="/my-discussions" className="text-sm font-semibold uppercase tracking-wide text-gray-300 hover:text-white transition-colors" data-testid="nav-discussions">
                Discussions
              </Link>
            )}
            {['super_admin', 'finance_admin', 'translator_admin', 'support_admin'].includes(user?.role) && (
              <Link to="/admin" className="text-sm font-semibold uppercase tracking-wide text-yellow-400 hover:text-yellow-300 transition-colors" data-testid="nav-admin">
                {t('adminPanel')}
              </Link>
            )}
          </div>

          {/* Right controls */}
          <div className="hidden md:flex items-center gap-2">
            {/* Language dropdown */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangMenuOpen(v => !v)}
                className="flex items-center gap-1.5 text-xs font-bold uppercase text-gray-400 hover:text-white px-2.5 py-1.5 rounded border border-gray-700 hover:border-gray-500 transition-colors"
                data-testid="lang-switcher"
              >
                <Globe size={12} />
                <span>{currentLang.flag}</span>
                <span>{currentLang.code.toUpperCase()}</span>
                <ChevronDown size={10} className={`transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {langMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-40 bg-[#0A0A0A] border border-gray-800 rounded-lg shadow-xl z-50 overflow-hidden" data-testid="lang-dropdown">
                  {LANGUAGES.map(l => (
                    <button
                      key={l.code}
                      onClick={() => { setLanguage(l.code); setLangMenuOpen(false); }}
                      className={`flex items-center justify-between w-full px-3 py-2.5 text-xs font-medium hover:bg-white/5 transition-colors ${l.code === lang ? 'text-white' : 'text-gray-400'}`}
                      data-testid={`lang-option-${l.code}`}
                    >
                      <span className="flex items-center gap-2"><span>{l.flag}</span><span>{l.label}</span></span>
                      {l.code === lang && <Check size={11} className="text-red-400" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Currency switcher */}
            <select
              value={currency}
              onChange={e => setCurrency(e.target.value)}
              className="text-xs font-bold uppercase bg-transparent text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 rounded px-2 py-1.5 transition-colors cursor-pointer"
              data-testid="currency-switcher"
              style={{ background: 'transparent' }}
            >
              {['EUR', 'PLN', 'CHF', 'GBP', 'USD'].map(c => (
                <option key={c} value={c} style={{ background: '#111' }}>{c}</option>
              ))}
            </select>

            {/* Auth */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded border border-gray-700 hover:border-red-500 transition-colors"
                  data-testid="user-menu-btn"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <User size={14} className="text-gray-400" />
                  )}
                  <span className="text-sm font-semibold text-white max-w-24 truncate">{user.name}</span>
                  <ChevronDown size={12} className="text-gray-400" />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-[#0A0A0A] border border-gray-800 rounded-lg shadow-xl z-50" data-testid="user-dropdown">
                    <Link to="/my-tickets" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors">
                      <Ticket size={14} /> {t('myTickets')}
                    </Link>
                    {(user.role === 'organiser' || user.role === 'super_admin') && (
                      <Link to="/organiser" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors">
                        <LayoutDashboard size={14} /> {t('organiserDashboard')}
                      </Link>
                    )}
                    {(['organiser', 'super_admin'].includes(user.role) || user.is_dj) && (
                      <Link to="/recurring-series" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors">
                        <LayoutDashboard size={14} /> {user.is_dj && user.role !== 'organiser' && user.role !== 'super_admin' ? 'DJ Dashboard' : 'Series'}
                      </Link>
                    )}
                    <Link to="/my-discussions" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors">
                      <Ticket size={14} /> Discussions
                    </Link>
                    <Link to="/account" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors">
                      <Settings size={14} /> Account Settings
                    </Link>
                    {['super_admin', 'finance_admin', 'translator_admin', 'support_admin'].includes(user.role) && (
                      <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-yellow-400 hover:text-yellow-300 hover:bg-white/5 transition-colors">
                        <Shield size={14} /> {t('adminPanel')}
                      </Link>
                    )}
                    <hr className="border-gray-800 my-1" />
                    <button onClick={handleLogout} className="flex items-center gap-2 w-full px-4 py-3 text-sm text-red-400 hover:text-red-300 hover:bg-white/5 transition-colors" data-testid="logout-btn">
                      <LogOut size={14} /> {t('logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/auth" className="btn-secondary text-xs px-4 py-2 rounded" data-testid="nav-signin">{t('signIn')}</Link>
                <Link to="/auth?tab=register" className="btn-primary text-xs px-4 py-2 rounded" data-testid="nav-signup">{t('signUp')}</Link>
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-white p-2" data-testid="mobile-menu-btn">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#050505] border-t border-gray-800 px-4 py-4 space-y-3" data-testid="mobile-menu">
          <Link to="/explore" onClick={() => setMobileOpen(false)} className="block text-sm font-bold uppercase text-gray-300 py-2">{t('explore')}</Link>
          <Link to="/feed" onClick={() => setMobileOpen(false)} className="block text-sm font-bold uppercase text-gray-300 py-2">Feed</Link>
          {user && <Link to="/my-tickets" onClick={() => setMobileOpen(false)} className="block text-sm font-bold uppercase text-gray-300 py-2">{t('myTickets')}</Link>}
          {(user?.role === 'organiser' || user?.role === 'super_admin') && (
            <Link to="/organiser" onClick={() => setMobileOpen(false)} className="block text-sm font-bold uppercase text-gray-300 py-2">{t('organiserDashboard')}</Link>
          )}
          {(['organiser', 'super_admin'].includes(user?.role) || user?.is_dj) && (
            <Link to="/recurring-series" onClick={() => setMobileOpen(false)} className="block text-sm font-bold uppercase text-gray-300 py-2">{user?.is_dj && user?.role !== 'organiser' && user?.role !== 'super_admin' ? 'DJ Dashboard' : 'Series'}</Link>
          )}
          {user && (
            <Link to="/my-discussions" onClick={() => setMobileOpen(false)} className="block text-sm font-bold uppercase text-gray-300 py-2">Discussions</Link>
          )}
          {user && (
            <Link to="/account" onClick={() => setMobileOpen(false)} className="block text-sm font-bold uppercase text-gray-300 py-2">Account Settings</Link>
          )}
          {['super_admin', 'finance_admin', 'translator_admin', 'support_admin'].includes(user?.role) && (
            <Link to="/admin" onClick={() => setMobileOpen(false)} className="block text-sm font-bold uppercase text-yellow-400 py-2">{t('adminPanel')}</Link>
          )}
          {/* Mobile language picker */}
          <div className="flex flex-wrap gap-2 pt-2">
            {LANGUAGES.map(l => (
              <button
                key={l.code}
                onClick={() => { setLanguage(l.code); setMobileOpen(false); }}
                className={`text-xs font-bold px-2.5 py-1.5 rounded border transition-colors ${l.code === lang ? 'border-red-600 text-white bg-red-600/10' : 'border-gray-700 text-gray-400'}`}
              >
                {l.flag} {l.code.toUpperCase()}
              </button>
            ))}
          </div>
          <select value={currency} onChange={e => setCurrency(e.target.value)} className="text-xs font-bold uppercase bg-transparent text-gray-400 border border-gray-700 rounded px-2 py-1.5 mt-1" style={{ background: '#111' }}>
            {['EUR', 'PLN', 'CHF', 'GBP', 'USD'].map(c => <option key={c} value={c} style={{ background: '#111' }}>{c}</option>)}
          </select>
          {!user ? (
            <div className="flex gap-2 pt-2">
              <Link to="/auth" onClick={() => setMobileOpen(false)} className="btn-secondary text-xs px-4 py-2 rounded flex-1 text-center">{t('signIn')}</Link>
              <Link to="/auth?tab=register" onClick={() => setMobileOpen(false)} className="btn-primary text-xs px-4 py-2 rounded flex-1 text-center">{t('signUp')}</Link>
            </div>
          ) : (
            <button onClick={handleLogout} className="block w-full text-left text-sm font-bold uppercase text-red-400 py-2">{t('logout')}</button>
          )}
        </div>
      )}
    </nav>
  );
};
